const WpSelectableRows = (() => {
    // Limitation: when used inside paginated DataTables, submit only includes
    // rows present on the currently rendered page.
    const containerSelector = '[data-wp-toggle~="selectableRows"]';
    const rowSelector = '[data-selectable-row]';
    const checkboxSelector = '[data-selectable-checkbox]';
    const interactiveSelector = 'a, button, input, select, textarea, label';
    const generatedInputSelector = '[data-selectable-generated-input]';
    const lastClickedRowByContainer = new WeakMap();
    const stateByContainer = new WeakMap();
    let refreshScheduled = false;
    let generatedContainerIdCounter = 0;

    const getContainers = (context = document) => {
        if (context.matches && context.matches(containerSelector)) {
            return [context];
        }

        return Array.from(context.querySelectorAll(containerSelector));
    };

    const getRows = (container) => {
        return Array.from(container.querySelectorAll(rowSelector));
    };

    const getContainerTable = (container) => {
        if (!container) {
            return null;
        }

        return container.closest('table');
    };

    const getDataTableRows = (container) => {
        const table = getContainerTable(container);

        if (!table) {
            return null;
        }

        if (window.jQuery && window.jQuery.fn && window.jQuery.fn.dataTable && window.jQuery.fn.dataTable.isDataTable(table)) {
            const api = window.jQuery(table).DataTable();

            if (api && typeof api.rows === 'function') {
                return Array.from(api.rows({ search: 'applied' }).nodes()).filter((row) => row.matches && row.matches(rowSelector));
            }
        }

        if (window.DataTable && typeof window.DataTable.tables === 'function') {
            const apiTables = window.DataTable.tables({ api: true });

            if (apiTables && typeof apiTables.table === 'function') {
                const apiTable = apiTables.table(table);

                if (apiTable && typeof apiTable.rows === 'function') {
                    return Array.from(apiTable.rows({ search: 'applied' }).nodes()).filter((row) => row.matches && row.matches(rowSelector));
                }
            }
        }

        return null;
    };

    const getAllRows = (container) => {
        return getDataTableRows(container) || getRows(container);
    };

    const getSelectableCheckedModes = (container) => {
        return new Set((container.dataset.selectableChecked || '').split(/\s+/).filter(Boolean));
    };

    const getRowValue = (row) => {
        return row.dataset.selectableRow || '';
    };

    const getSelectableName = (row, container) => {
        return container.dataset.selectableName || '';
    };

    const ensureContainerId = (container) => {
        if (container.dataset.selectableContainerId) {
            return container.dataset.selectableContainerId;
        }

        generatedContainerIdCounter += 1;
        container.dataset.selectableContainerId = 'selectable-' + generatedContainerIdCounter;
        return container.dataset.selectableContainerId;
    };

    const isRowAvailable = (row) => {
        return !row.classList.contains('d-none');
    };

    const getCheckbox = (row) => {
        return row.querySelector(checkboxSelector);
    };

    const ensureCheckbox = (row) => {
        const existingCheckbox = getCheckbox(row);

        if (existingCheckbox) {
            return existingCheckbox;
        }

        const checkbox = document.createElement('input');
        checkbox.setAttribute('data-selectable-checkbox', '');
        checkbox.hidden = true;
        checkbox.type = 'checkbox';
        checkbox.value = getRowValue(row);
        row.prepend(checkbox);

        return checkbox;
    };

    const createState = (container) => ({
        modes: getSelectableCheckedModes(container),
        onStartActivated: false,
        manualSelected: new Set(),
        implicitDeselected: new Set(),
        knownRows: new Map()
    });

    const getState = (container) => {
        if (!stateByContainer.has(container)) {
            stateByContainer.set(container, createState(container));
        }

        return stateByContainer.get(container);
    };

    const isDefaultOn = (container) => {
        return getState(container).modes.has('defaultOn');
    };

    const isOnStartPending = (container) => {
        const state = getState(container);
        return state.modes.has('onStart') && !state.onStartActivated;
    };

    const isImplicitAllSelected = (container) => {
        if (isDefaultOn(container)) {
            return true;
        }

        if (isOnStartPending(container)) {
            return true;
        }

        const state = getState(container);
        return state.modes.has('onEmpty') && state.manualSelected.size === 0;
    };

    const shouldDisplayAllCount = (container) => {
        if (isOnStartPending(container)) {
            return true;
        }

        const state = getState(container);
        return state.modes.has('onEmpty') && state.manualSelected.size === 0;
    };

    const reconcileContainer = (container) => {
        const state = getState(container);
        const knownRows = new Map();

        getAllRows(container).forEach((row) => {
            const checkbox = ensureCheckbox(row);
            const value = getRowValue(row);
            const name = getSelectableName(row, container);

            if (!value) {
                return;
            }

            knownRows.set(value, { value, name, row });
        });

        state.knownRows = knownRows;
        state.manualSelected.forEach((value) => {
            if (!knownRows.has(value)) {
                state.manualSelected.delete(value);
            }
        });
        state.implicitDeselected.forEach((value) => {
            if (!knownRows.has(value)) {
                state.implicitDeselected.delete(value);
            }
        });
    };

    const isRowChecked = (container, row) => {
        const state = getState(container);
        const value = getRowValue(row);

        if (!value) {
            return false;
        }

        if (!isRowAvailable(row)) {
            return false;
        }

        const checkbox = ensureCheckbox(row);
        if (checkbox.disabled) {
            return false;
        }

        if (isDefaultOn(container)) {
            return !state.implicitDeselected.has(value);
        }

        if (isOnStartPending(container)) {
            return true;
        }

        if (state.modes.has('onEmpty') && state.manualSelected.size === 0) {
            return true;
        }

        return state.manualSelected.has(value);
    };

    const setRowChecked = (container, row, checked) => {
        const state = getState(container);
        const value = getRowValue(row);

        if (!value) {
            return;
        }

        if (isDefaultOn(container)) {
            if (checked) {
                state.implicitDeselected.delete(value);
            } else {
                state.implicitDeselected.add(value);
            }

            return;
        }

        if (isOnStartPending(container)) {
            state.onStartActivated = true;
            state.manualSelected.clear();
        }

        if (checked) {
            state.manualSelected.add(value);
        } else {
            state.manualSelected.delete(value);
        }
    };

    const getNextCheckedState = (container, row) => {
        if (isDefaultOn(container)) {
            return !isRowChecked(container, row);
        }

        if (isImplicitAllSelected(container)) {
            return true;
        }

        return !isRowChecked(container, row);
    };

    const applyRowState = (container, row) => {
        const checkbox = ensureCheckbox(row);
        const checked = isRowChecked(container, row);
        const visuallySelected = isImplicitAllSelected(container) ? false : checked;

        checkbox.checked = checked;
        row.classList.toggle('is-selected', visuallySelected);
    };

    const updateResults = (container) => {
        const rows = getAllRows(container);
        const availableRowsCount = rows.reduce((count, row) => {
            const checkbox = ensureCheckbox(row);
            return count + (isRowAvailable(row) && !checkbox.disabled ? 1 : 0);
        }, 0);

        const selectedCount = rows.reduce((count, row) => {
            return count + (isRowChecked(container, row) ? 1 : 0);
        }, 0);

        const resultElement = container.querySelector('[data-selectable-results]');

        if (resultElement) {
            resultElement.textContent = shouldDisplayAllCount(container) ? availableRowsCount : selectedCount;
        }

        if (container.dataset.selectableTargetCount) {
            const value = shouldDisplayAllCount(container) ? availableRowsCount : selectedCount;

            document.querySelectorAll(container.dataset.selectableTargetCount).forEach((element) => {
                element.textContent = value;
            });
        }
    };

    const updateHiddenInput = (container) => {
        const form = container.closest('form');
        const selectableName = container.dataset.selectableName || '';

        if (!form) {
            return;
        }

        const containerId = ensureContainerId(container);
        let input = form.querySelector(`${generatedInputSelector}[data-selectable-owner="${containerId}"]`);

        if (!selectableName) {
            if (input) {
                input.remove();
            }
            return;
        }

        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.setAttribute('data-selectable-generated-input', '');
            input.setAttribute('data-selectable-owner', containerId);
            form.append(input);
        }

        input.name = selectableName;
        input.value = getSelectedEntries(container).map((entry) => entry.value).join(',');
    };

    const refreshContainer = (container) => {
        reconcileContainer(container);

        getRows(container).forEach((row) => {
            applyRowState(container, row);
        });

        updateResults(container);
        updateHiddenInput(container);

        const lastClickedRow = lastClickedRowByContainer.get(container);
        if (lastClickedRow && !container.contains(lastClickedRow)) {
            lastClickedRowByContainer.delete(container);
        }
    };

    const refresh = (context = document) => {
        getContainers(context).forEach((container) => {
            refreshContainer(container);
        });
    };

    const sync = (context = document) => {
        refresh(context);
    };

    const scheduleSync = () => {
        if (refreshScheduled) {
            return;
        }

        refreshScheduled = true;

        requestAnimationFrame(() => {
            refreshScheduled = false;
            sync();
        });
    };

    const toggleRange = (container, currentRow) => {
        const anchorRow = lastClickedRowByContainer.get(container);

        if (!anchorRow || anchorRow === currentRow) {
            return false;
        }

        const rows = getRows(container);
        const anchorIndex = rows.indexOf(anchorRow);
        const currentIndex = rows.indexOf(currentRow);

        if (anchorIndex === -1 || currentIndex === -1) {
            return false;
        }

        const nextChecked = getNextCheckedState(container, currentRow);
        const startIndex = Math.min(anchorIndex, currentIndex);
        const endIndex = Math.max(anchorIndex, currentIndex);

        rows.slice(startIndex, endIndex + 1).forEach((row) => {
            const checkbox = ensureCheckbox(row);

            if (!isRowAvailable(row) || checkbox.disabled) {
                return;
            }

            setRowChecked(container, row, nextChecked);
        });

        refreshContainer(container);
        lastClickedRowByContainer.set(container, currentRow);

        return true;
    };

    const handleRowClick = (event) => {
        const row = event.target.closest(rowSelector);

        if (!row || !isRowAvailable(row)) {
            return;
        }

        const container = row.closest(containerSelector);

        if (!container || event.target.closest(interactiveSelector)) {
            return;
        }

        if (event.shiftKey && toggleRange(container, row)) {
            return;
        }

        const checkbox = ensureCheckbox(row);

        if (checkbox.disabled) {
            return;
        }

        setRowChecked(container, row, getNextCheckedState(container, row));
        refreshContainer(container);
        lastClickedRowByContainer.set(container, row);
    };

    const handleCheckboxChange = (event) => {
        const checkbox = event.target.closest(checkboxSelector);

        if (!checkbox) {
            return;
        }

        const row = checkbox.closest(rowSelector);

        if (!row) {
            return;
        }

        const container = row.closest(containerSelector);

        if (!container) {
            return;
        }

        if (checkbox.disabled || !isRowAvailable(row)) {
            checkbox.checked = false;
            refreshContainer(container);
            return;
        }

        setRowChecked(container, row, checkbox.checked);
        refreshContainer(container);
        lastClickedRowByContainer.set(container, row);
    };

    const getSelectedEntries = (container) => {
        const state = getState(container);
        const entries = [];

        state.knownRows.forEach((entry, value) => {
            if (isDefaultOn(container)) {
                if (entry.row && isRowChecked(container, entry.row)) {
                    entries.push(entry);
                }

                return;
            }

            if (isOnStartPending(container)) {
                if (entry.row && isRowChecked(container, entry.row)) {
                    entries.push(entry);
                }
                return;
            }

            if (state.modes.has('onEmpty') && state.manualSelected.size === 0) {
                if (entry.row && isRowChecked(container, entry.row)) {
                    entries.push(entry);
                }
                return;
            }

            if (state.manualSelected.has(value) && entry.row && isRowChecked(container, entry.row)) {
                entries.push(entry);
            }
        });

        return entries;
    };

    const init = () => {
        document.addEventListener('click', handleRowClick);
        document.addEventListener('change', handleCheckboxChange);

        const observer = new MutationObserver((mutations) => {
            const shouldSync = mutations.some((mutation) => {
                if (mutation.type !== 'childList') {
                    return false;
                }

                const changedNodes = [
                    ...Array.from(mutation.addedNodes),
                    ...Array.from(mutation.removedNodes)
                ];

                return changedNodes.some((node) => {
                    return node.nodeType === Node.ELEMENT_NODE
                        && !node.matches(generatedInputSelector)
                        && !node.querySelector?.(generatedInputSelector);
                });
            });

            if (shouldSync) {
                scheduleSync();
            }
        });

        const start = () => {
            const root = document.body || document.documentElement;

            if (!root) {
                return;
            }

            observer.observe(root, {
                childList: true,
                subtree: true
            });

            sync();
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', start, { once: true });
            return;
        }

        start();
    };

    return { init, refresh, sync };
})();

window.WpSelectableRows = WpSelectableRows;
WpSelectableRows.init();
